import { SearchFilterTag } from '@/components';
import { fetchGovernmentNumber } from '@/features/government-search/_utils/search-government';
import type { SharedSearchFormType } from '@/features/search/_models/shared-search';
import { t } from '@/i18n';
import { type FC, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

/**
 * KS 分析タブ用の「支出先名」タグ。
 * RS 用 `PaymentTag` は `ProjectSearchFormType` の `fundSheetType` を参照するが、
 * KS 分析タブのフォームには存在しないため KS 専用に切り出している。
 */
export const KsPaymentTag: FC<{ onResetPayment: () => void }> = ({
  onResetPayment,
}) => {
  const { control: sharedSearchControl, setValue: setSharedSearchValue } =
    useFormContext<SharedSearchFormType>();
  const [corporateName, setCorporateName] = useState<string | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(true);
  const corporateNumber = useWatch({
    control: sharedSearchControl,
    name: 'corporateNumber',
  });
  // 連続して corporateNumber が変わったときに古いレスポンスを破棄するためのリクエスト ID。
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (corporateNumber) {
      setIsLoading(true);
      (async () => {
        const res = await fetchGovernmentNumber({ corporateNumber });
        // このレスポンスを反映する前に、より新しいリクエストが走っていないか確認する
        if (requestId !== requestIdRef.current) {
          return;
        }
        setCorporateName(res.at(0)?.corporateName);
        setIsLoading(false);
      })();
    } else {
      setCorporateName(undefined);
      setIsLoading(false);
    }
  }, [corporateNumber]);

  if (!corporateName || isLoading) {
    return null;
  }

  return (
    <SearchFilterTag
      closable={true}
      label={`${t('payment-name')}: ${corporateName}`}
      onClose={() => {
        setSharedSearchValue('corporateNumber', null);
        onResetPayment();
      }}
    />
  );
};
